/**
 * `ui/menu.js`
 * - メニュー
 *
 * 使用方法:
 * - import {menu} from "ui/menu.js";
 */


import {addClass, removeClass, lockScroll, unlockScroll} from "../utils/index.js";
import {gsap} from "../modules/gsap/gsap.js";
import {barbaEventObserver} from "../core/barba/utils/barbaEventObserver.js";

export class Menu {
  constructor() {
    this.body = document.body;
    this.openBtns = document.querySelectorAll(".js-menu-btn-open");
    this.closeBtns = document.querySelectorAll(".js-menu-btn-close, .menu_content_inner a");
    this.menu = document.querySelector(".menu_content");
    this.menuBg = document.querySelector(".menu_bg");
    this.menuFigure = document.querySelector(".menu_figure");
    this.mainLists = this.menu?.querySelectorAll(".menu_nav_list_ttl, .menu_nav_list_child_link, .btn_rect");
    this.openBtn = document.querySelector("#menu-open-btn");
    this.closeBtn = document.querySelector("#menu-close-btn");
    this.listeners = [];
    this.tl = null; // GSAPタイムライン

    if (this.openBtns.length && this.menu) {
      this.init();
    }
  }

  /**
   * 初期化処理
   */
  init() {
    gsap.set(".js-menu-btn-close", { pointerEvents: "none" });

    this.openBtns.forEach((btn) => {
      btn.addEventListener("click", () => this.open());
      this.listeners.push([btn, "click", this.open.bind(this)]);
    });

    this.closeBtns.forEach((btn) => {
      btn.addEventListener("click", () => this.close());
      this.listeners.push([btn, "click", this.close.bind(this)]);
    });

    // メニュー内のスクロールをLenisに伝播させない
    this.menu.addEventListener("wheel", (e) => e.stopPropagation(), { passive: true });

    barbaEventObserver.observeEvents(this.listeners);
  }

  /**
   * メニューを開く
   */
  open() {
    this.body.classList.add("menu-open");
    this.body.classList.remove("menu-close");
    this.animateOpen();
  }

  /**
   * メニューを閉じる
   */
  close() {
    if (this.body.classList.contains("menu-open")) {
      this.body.classList.remove("menu-open");
      this.body.classList.add("menu-close");
      this.animateClose();
    }
  }

  /**
   * メニューを開くアニメーション
   */
  animateOpen() {
    if (this.tl) this.tl.kill();

    this.tl = gsap.timeline({ repeat: 0, ease: "curtain" });

    gsap.set(this.menu, { autoAlpha: 1 });
    gsap.set(this.openBtn, { pointerEvents: "none" });
    gsap.set(this.closeBtn, { pointerEvents: "auto" });

    this.tl
      .call(addClass, [this.openBtn, "ing"])
      .add(lockScroll, "<") // スクロール禁止
      .fromTo(
        this.openBtn,
        { clipPath: "inset(0% 0% 0% 0%)" },
        { clipPath: "inset(0% 50% 0% 50%)", duration: 0.4 },
        "<"
      )
      .to(this.menuBg, { y: "0%", height: "100%", autoAlpha: 1, duration: 0.5 }, "<")
      .fromTo(this.menu, { autoAlpha: 0 }, { autoAlpha: 1 }, "<")
      .fromTo(this.menuFigure, { autoAlpha: 0, clipPath: "inset(0% 0% 100% 0%)" }, { autoAlpha: 1, clipPath: "inset(0% 0% 0% 0%)" }, "<")
      .fromTo(
        this.mainLists,
        { x: "10%", autoAlpha: 0.2, clipPath: "inset(0% 100% 0% 0%)" },
        { x: "0%", autoAlpha: 1, clipPath: "inset(0% 0% 0% 0%)", duration: 0.5, stagger: { each: 0.06 } },
        "<"
      )
      .fromTo(
        this.closeBtn,
        { clipPath: "inset(0% 50% 0% 50%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 0.4 },
        ">"
      )
      .call(removeClass, [this.openBtn, "ing"], ">");
  }

  /**
   * メニューを閉じるアニメーション
   */
  animateClose() {
    if (this.tl) this.tl.kill();

    this.tl = gsap.timeline({ repeat: 0, ease: "curtain" });

    gsap.set(this.closeBtn, { pointerEvents: "none" });
    gsap.set(this.openBtn, { pointerEvents: "auto" });

    this.tl
      .call(addClass, [this.openBtn, "ing"])
      .add(unlockScroll, "<") // スクロール解除
      .fromTo(
        this.closeBtn,
        { clipPath: "inset(0% 0% 0% 0%)" },
        { clipPath: "inset(0% 50% 0% 50%)", duration: 0.6 },
        "<"
      )
      .fromTo(
        this.mainLists,
        { x: "0%", autoAlpha: 1, clipPath: "inset(0% 0% 0% 0%)" },
        { autoAlpha: 0.5, clipPath: "inset(0% 100% 0% 0%)", duration: 0.5 },
        "<"
      )
      .fromTo(this.menuFigure, { autoAlpha: 1, clipPath: "inset(0% 0% 0% 0%)" }, { autoAlpha: 0, clipPath: "inset(0% 0% 100% 0%)" }, "<")
      .to(this.menuBg, { y: "0%", height: document.querySelector("header").clientHeight, autoAlpha: 0.9, duration: 0.5 }, "<")
      .fromTo(
        this.openBtn,
        { clipPath: "inset(0% 50% 0% 50%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 0.6 },
        ">"
      )
      .to(this.menu, { autoAlpha: 0 }, "<")
      .call(removeClass, [this.openBtn, "ing"], ">");
  }
}

/** 初期化処理をエクスポート */
export const initMenu = () => new Menu();
